import { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    TextField,
    Chip,
    Button,
    IconButton,
    InputAdornment,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { updateCommonFields, updatePiiFields, getGlobalConfig } from '../../services/api';

/**
 * FieldListEditor Component
 * 
 * Provides editing for common columns and PII fields
 * with two-column layout (Legacy | Catalyst)
 */
function FieldListEditor({ title, description, fields, fieldType, onUpdate, showNotification }) {
    const [legacyFields, setLegacyFields] = useState(fields?.legacy || []);
    const [catalystFields, setCatalystFields] = useState(fields?.catalyst || []);
    const [newLegacyField, setNewLegacyField] = useState('');
    const [newCatalystField, setNewCatalystField] = useState('');
    const [legacySearch, setLegacySearch] = useState('');
    const [catalystSearch, setCatalystSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    const handleAddLegacyField = () => {
        if (newLegacyField.trim() && !legacyFields.includes(newLegacyField.trim())) {
            setLegacyFields([...legacyFields, newLegacyField.trim()]);
            setNewLegacyField('');
            setHasChanges(true);
        }
    };

    const handleAddCatalystField = () => {
        if (newCatalystField.trim() && !catalystFields.includes(newCatalystField.trim())) {
            setCatalystFields([...catalystFields, newCatalystField.trim()]);
            setNewCatalystField('');
            setHasChanges(true);
        }
    };

    const handleRemoveLegacyField = (fieldToRemove) => {
        setLegacyFields(legacyFields.filter(f => f !== fieldToRemove));
        setHasChanges(true);
    };

    const handleRemoveCatalystField = (fieldToRemove) => {
        setCatalystFields(catalystFields.filter(f => f !== fieldToRemove));
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const updateFn = fieldType === 'common-fields' ? updateCommonFields : updatePiiFields;
            await updateFn({
                description: fields?.description || description,
                legacy: legacyFields,
                catalyst: catalystFields,
            });
            const config = await getGlobalConfig();
            onUpdate(config);
            setHasChanges(false);
            showNotification(`${title} saved successfully`);
        } catch (err) {
            showNotification(err.response?.data?.detail || 'Failed to save', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setLegacyFields(fields?.legacy || []);
        setCatalystFields(fields?.catalyst || []);
        setHasChanges(false);
    };

    const filteredLegacyFields = legacySearch
        ? legacyFields.filter(f => f.toLowerCase().includes(legacySearch.toLowerCase()))
        : legacyFields;

    const filteredCatalystFields = catalystSearch
        ? catalystFields.filter(f => f.toLowerCase().includes(catalystSearch.toLowerCase()))
        : catalystFields;

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {fields?.description || description}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {hasChanges && (
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={handleReset}
                        >
                            Reset
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {hasChanges && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    You have unsaved changes. Click "Save Changes" to persist them.
                </Alert>
            )}

            {/* Two-column layout */}
            <Grid container spacing={3}>
                {/* Legacy Column */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%', minHeight: 400 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                            Legacy ({legacyFields.length})
                        </Typography>

                        {/* Search */}
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search legacy fields..."
                            value={legacySearch}
                            onChange={(e) => setLegacySearch(e.target.value)}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Add field */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Add new field..."
                                value={newLegacyField}
                                onChange={(e) => setNewLegacyField(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleAddLegacyField();
                                }}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleAddLegacyField}
                                disabled={!newLegacyField.trim()}
                            >
                                <AddIcon />
                            </Button>
                        </Box>

                        {/* Field chips */}
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            maxHeight: 300,
                            overflowY: 'auto',
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                        }}>
                            {filteredLegacyFields.map((field, idx) => (
                                <Chip
                                    key={idx}
                                    label={field}
                                    size="small"
                                    onDelete={() => handleRemoveLegacyField(field)}
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            ))}
                            {filteredLegacyFields.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                    {legacySearch ? 'No matching fields' : 'No fields defined'}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Catalyst Column */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, height: '100%', minHeight: 400 }}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                            Catalyst ({catalystFields.length})
                        </Typography>

                        {/* Search */}
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Search catalyst fields..."
                            value={catalystSearch}
                            onChange={(e) => setCatalystSearch(e.target.value)}
                            sx={{ mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Add field */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Add new field..."
                                value={newCatalystField}
                                onChange={(e) => setNewCatalystField(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') handleAddCatalystField();
                                }}
                                sx={{ flex: 1 }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleAddCatalystField}
                                disabled={!newCatalystField.trim()}
                            >
                                <AddIcon />
                            </Button>
                        </Box>

                        {/* Field chips */}
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            maxHeight: 300,
                            overflowY: 'auto',
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                        }}>
                            {filteredCatalystFields.map((field, idx) => (
                                <Chip
                                    key={idx}
                                    label={field}
                                    size="small"
                                    onDelete={() => handleRemoveCatalystField(field)}
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            ))}
                            {filteredCatalystFields.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                    {catalystSearch ? 'No matching fields' : 'No fields defined'}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default FieldListEditor;
